import { useApiMutation } from "@gladis/api-client";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { CurrentUserDto, HouseholdDto } from "@gladis/shared";

/**
 * The active-household header, now switchable (AC-H9).
 *
 * Feature 004 shipped this as a label with no affordance, because the constitution ruled a switcher
 * out of scope at the time. v1.1.0 corrected that — the prohibition appeared in neither authority
 * Principle VII defers to — so the label becomes a control.
 *
 * A person with ONE household still sees exactly what 004 rendered: a name, no menu, no button.
 * Offering a switcher to someone with nothing to switch to would be a control that exists only to
 * be disappointing.
 *
 * WHAT THIS COMPONENT DOES NOT DO, and it is the point: it does not decide which household the user
 * is in. It sends a preference to the server, which validates it against membership and returns the
 * whole `CurrentUserDto` back. The list it renders is the server's `households` — already filtered
 * by row-level security — so there is no id here that the server has not already vouched for.
 */
export function HouseholdSwitcher({
  me,
  onCreateHousehold,
}: {
  me: CurrentUserDto;
  onCreateHousehold?: () => void;
}): ReactNode {
  const t = useT();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  /**
   * `returnFocus` IS A PARAMETER BECAUSE THE TWO WAYS OUT WANT OPPOSITE THINGS.
   *
   * Escape must put focus back on the trigger: the element the keyboard was on has just been
   * unmounted, and focus left on a removed node falls to `<body>`, which strands a keyboard user at
   * the top of the document with no way back to where they were.
   *
   * A pointer press outside must NOT, because the press is already on its way to whatever was
   * clicked. Pulling focus back to the trigger would take it away from the control the member is in
   * the act of choosing.
   */
  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  /*
   * ==========================================================================================
   * THE MENU HAD NO WAY OUT BUT THE TRIGGER
   * ==========================================================================================
   *
   * Escape did nothing and a click anywhere else did nothing: the only dismissal was pressing the
   * same button a second time. That is not a small omission — Escape and click-away are how every
   * other menu in every application behaves, so a member who does either sees a menu that has
   * decided to stay, and a keyboard user who cannot see it has no exit at all.
   *
   * `pointerdown` rather than `click`, matching `ActionMenu` in `@gladis/ui`: a press that starts
   * outside and releases inside never fires a `click` on the document, so a drag out of the menu
   * would leave it open. Registering on the DOCUMENT rather than on a backdrop element keeps the
   * page underneath live — a transparent backdrop would swallow the first click every time.
   *
   * There is no listener at all while the menu is closed. The effect's guard returns before
   * registering, so the common case — one household, or a menu nobody has opened — costs nothing.
   *
   * NO SCROLL HANDLER, unlike `ActionMenu`. That menu is `position: fixed` and portalled, so it has
   * to be re-measured as the page moves; this one is `absolute` inside the trigger's own `relative`
   * box and travels with it for free.
   */
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      // The trigger's own press is left alone: its `onClick` toggles, and closing here as well would
      // close and immediately reopen — a menu that flickers and never shuts.
      if (triggerRef.current?.contains(target) === true) return;
      if (menuRef.current?.contains(target) === true) return;
      close(false);
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") close(true);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const switchTo = useApiMutation<CurrentUserDto, { householdId: string }>(
    (client, body) => client.put("/me/active-household", body),
    {
      // Seeded so the new household name appears immediately rather than after a refetch.
      seeds: ["me"],
      // EVERY cached query is dropped, not just `me`. Each one was fetched in the previous
      // household's scope, and a stale entry surviving the switch would render another household's
      // numbers under this household's name — the one failure mode a switcher must never have
      // (FR-036). Naming keys instead would mean a list that goes stale the moment 006 adds a query.
      discardsEverything: true,
      onSuccess: () => setOpen(false),
    },
  );

  const active = me.activeHousehold;
  if (!active) return null;

  // ONE household means NO control — plain text, exactly what 004 shipped (US4 scenario 1).
  //
  // The condition is `households.length` and deliberately NOT "…or we were given a create
  // callback". Making the header a control for someone with nothing to switch between changes the
  // shipped appearance of every screen for the single-household case, which is the overwhelmingly
  // common one in this product. Creating an ADDITIONAL household is offered from Household
  // Settings instead — the page that is already about managing households.
  if (me.households.length < 2) {
    return (
      // No margin. It used to carry `mb-2` from when it was the first thing inside the content
      // column and had to clear the page title; it now sits in the shell's own top bar, which is a
      // centred flex row — a bottom margin there is an 8px push off centre and nothing else.
      //
      // `-mx-2` MATCHES the trigger's `px-2` below, so the name starts at the same x whether or not
      // this household has anywhere to switch to. Without it the two branches sit 8px apart, and
      // gaining a second household would visibly nudge the header sideways.
      <p className="-mx-2 px-2 text-sm text-muted" data-testid="active-household">
        {active.name}
      </p>
    );
  }

  return (
    // `relative` and nothing else: the menu below is absolutely positioned against it. The margin
    // this used to carry belonged to the layout it no longer sits in — see the single-household
    // branch above.
    <div className="relative">
      {/*
       * `-mx-2` against the `px-2` below. The padding is what gives the hover fill something to
       * fill — a hover state drawn tight to the glyphs reads as a highlight rather than as a button
       * — and the negative margin puts the name back on the same x as the page title beneath it, so
       * the control gains a hit area without moving any text.
       */}
      <button
        ref={triggerRef}
        type="button"
        className={
          "-mx-2 flex items-center gap-1.5 rounded-control px-2 py-1 text-sm text-muted " +
          "transition-colors hover:bg-accent hover:text-ink"
        }
        data-testid="active-household"
        aria-expanded={open}
        aria-haspopup="menu"
        // Only while open: `aria-controls` pointing at an element that is not in the document is a
        // dangling reference, and a screen reader following it finds nothing.
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        {active.name}
        {/*
         * `aria-hidden` because `aria-expanded` on the button already announces the state; a
         * chevron with a name of its own would say it a second time and less precisely.
         *
         * It ROTATES rather than swapping to a `ChevronUpIcon`. Two icons cannot be tweened, so the
         * arrow would jump between states — and the rotation is what ties the arrow to the panel
         * that is sliding into place under it. `duration-200` matches nothing else here on purpose:
         * it is short enough to have finished by the time the eye reaches the menu.
         */}
        <ChevronDownIcon
          aria-hidden="true"
          className={`size-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        /*
         * ==========================================================================================
         * `bg-page`, NOT `bg-panel` — THE MENU WAS SEE-THROUGH AND THE TOKEN IS WHY
         * ==========================================================================================
         *
         * `--bg-panel` is `transparent` in BOTH themes, deliberately: a panel sits over the animated
         * background and is supposed to let it through. A popover is the one surface in the product
         * for which that is exactly wrong — it overlaps the page rather than sitting in it, so the
         * household names were rendering over whatever text and waves happened to be behind them.
         * It looked like a missing colour and was a correct token used on the wrong kind of surface.
         *
         * `bg-page` is the opaque one — near-black in dark, paper in light — and it is what
         * `ActionMenu` in `@gladis/ui` already reaches for. Its border moves to `border-input` in
         * step, for the same reason: `border-line` is tuned to read against the live gradient, and
         * against an opaque fill it is the fainter of the two.
         */
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          // A name of its own: "menu" alone tells a screen-reader user nothing about what choosing
          // from it would do, and the trigger's name is the household rather than the action.
          aria-label={t("identity.household.switch")}
          className="absolute z-10 mt-1 min-w-56 rounded-control border border-input bg-page p-1 shadow-lg"
        >
          {me.households.map((household: HouseholdDto) => (
            <button
              key={household.id}
              type="button"
              role="menuitem"
              className="flex w-full items-center justify-between gap-2 rounded-control px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-60"
              disabled={switchTo.isPending || household.id === active.id}
              onClick={() => switchTo.mutate({ householdId: household.id })}
            >
              {household.name}
              {/*
               * The tick keeps its `aria-label`: unlike the chevron it is the ONLY thing saying
               * which household you are already in, and `disabled` alone does not say why.
               */}
              {household.id === active.id ? (
                <CheckIcon className="size-4 shrink-0 text-muted" aria-label="current" role="img" />
              ) : null}
            </button>
          ))}

          {onCreateHousehold ? (
            <button
              type="button"
              role="menuitem"
              className="mt-1 block w-full rounded-control border-t border-input px-3 py-2 text-left text-sm hover:bg-accent"
              onClick={() => {
                close(false);
                onCreateHousehold();
              }}
            >
              {t("identity.household.createNew")}
            </button>
          ) : null}
        </div>
      ) : null}

      {switchTo.error ? (
        <p className="mt-1 text-sm text-muted" role="alert">
          {errorSentence(switchTo.error)}
        </p>
      ) : null}
    </div>
  );
}
